import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  activeDateSelector,
  activePageDisplayNameSelector,
  activePageIdSelector,
  activeSectionSelector,
  createActiveFundSelectorBySection,
  createPageDetailsSelector,
  createSectionByIdSelector,
  createSectionFundIdsSelector,
  createSectionFundNamesSelector,
  hideStandardDatePickerSelector,
  isTitlebarLoadingSelector,
  titleBarDatePickersSelector,
  titleBarInputsSelector,
  titleBarSelectsSelector,
} from '../../../redux/pages/selectors';
import FundPicker from '../../selects/FundPicker';
import TitleBarSelect from '../../selects/TitleBarSelect';
import { zIndexes } from '../../../styling/zIndexes';
import {
  DatePicker,
  Input,
  Select,
} from '../../../types/redux/pages/PagesStore';
import RaptorLoading from '../../feedback/RaptorLoading';
import {
  updateActiveDate,
  updateActiveDateRangeEnd,
  updateActiveDateRangeStart,
  updateAvailableDates,
  updateSpecificTitleBarComponent,
  updateSpecificTitlebarInput,
} from '../../../redux/pages/actions';
import GeneralInput from '../../inputs/GeneralInput';

import { useLocation, useNavigate } from 'react-router-dom';
import { GeneralFileExportButton } from './components/FileExportButton';
import RaptorDatePicker from '../../selects/RaptorDatePicker.component';
import dayjs from 'dayjs';
import useRouteParams from '../../../hooks/useRouteParams';
import {
  getAvailableDateXMonthsAgo,
  getMostRecentAvailableDate,
} from '../../../utilities/dateUtilities';
import {
  DATE_FORMAT,
  getAllRelevantWeekdays,
} from '../../../utilities/dateUtilities';
import { useHorizontalScrollIndicators } from '../../../hooks/useHorizontalScrollIndicators';
import { FadeIndicator } from '../topBar/NavContainer.component';
import { ScrollArrowButton } from '../topBar/ScrollArrowButton';
import { raptorPlatformIdSelector } from '../../../redux/raptorPlatform/selectors';
import { usePositionDates } from '../../../api/position-dates';
import { createResourceCacheKey } from '../general/SimpleGeneralPageWrapper';
import Pill from '../../ui/Pill';

// This functions is used to generate all the dates between a start date and the current date for adding to RaptorDatePicker

// For some reason Raptor date picker is using the sunday as the first day of the week

const TitleBar: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    containerRef,
    showLeftIndicator,
    showRightIndicator,
    handleScrollLeft,
    handleScrollRight,
    checkForOverflow,
  } = useHorizontalScrollIndicators<HTMLDivElement>();

  const pageName = useSelector(activePageDisplayNameSelector);

  const activeSection = useSelector(activeSectionSelector);
  const pageId = useSelector(activePageIdSelector);
  const pageDetailSelector = createPageDetailsSelector(pageId, activeSection);
  const fundIdsSelector = createSectionFundIdsSelector(
    activeSection || '',
    pageId,
  );
  const fundNamesSelector = createSectionFundNamesSelector(
    activeSection || '',
    pageId,
  );
  const fundIds = useSelector(fundIdsSelector);
  const fundNames = useSelector(fundNamesSelector);
  const pageDetails = useSelector(pageDetailSelector);
  const activeFundSelector = createActiveFundSelectorBySection(
    activeSection,
    pageId,
  );
  const activeFund = useSelector(activeFundSelector);
  const selects = useSelector(titleBarSelectsSelector);
  const inputs = useSelector(titleBarInputsSelector);
  const datePickers = useSelector(titleBarDatePickersSelector);
  const activeDate = useSelector(activeDateSelector);
  const titleBarIsLoading = useSelector(isTitlebarLoadingSelector);
  const hideStandardDatePicker = useSelector(hideStandardDatePickerSelector);

  const routeParams = useRouteParams(['positionDate', 'fundId']);
  const sectionDetails = useSelector(
    createSectionByIdSelector(activeSection ?? ''),
  );
  const raptorPlatformId = useSelector(raptorPlatformIdSelector);

  // Prefer URL fundId (updated immediately by FundPicker) over Redux fund object
  const activeFundId = routeParams.fundId || activeFund?.id || null;

  // necessary states for handling dates
  const [weekdays] = useState<string[]>(getAllRelevantWeekdays());
  const [positionDates, setPositionsDates] = useState<string[]>(weekdays);
  const [positionDatesLoading, setPositionDatesLoading] =
    useState<boolean>(true);

  const [dateRangeStart, setDateRangeStart] = useState<string>('');
  const [dateRangeEnd, setDateRangeEnd] = useState<string>('');

  // Track if this is the initial app load (for URL → Redux initialization)
  const isInitialLoadRef = useRef(true);
  // Track last section to detect section changes
  const lastSectionRef = useRef<string | null>(null);
  // Track if we've initialized the date for the current section
  const dateInitializedForSectionRef = useRef<string | null>(null);
  // Track last synced URL to prevent feedback loops
  const lastSyncedUrlRef = useRef<string | null>(null);
  // Track last active fund so we can reset date ranges when the fund changes
  const lastActiveFundIdRef = useRef<string | null | undefined>(undefined);

  // Store routeParams in ref to read without triggering re-runs
  const routeParamsRef = useRef(routeParams);
  routeParamsRef.current = routeParams;

  useEffect(() => {
    dispatch(updateActiveDateRangeStart(dateRangeStart));
  }, [dateRangeStart]);

  useEffect(() => {
    dispatch(updateActiveDateRangeEnd(dateRangeEnd));
  }, [dateRangeEnd]);

  // Section-level dates (overview + per-fund map from get_position_dates)
  const rsResources = createResourceCacheKey(
    sectionDetails?.rsResources,
    pageDetails?.rsResources,
  );
  const {
    data: positionDatesMap,
    isLoading: isPositionDatesQueryLoading,
    error: positionDatesError,
  } = usePositionDates(
    {
      fundTypes: sectionDetails?.fundTypes,
      rsResources,
      raptorPlatformId: raptorPlatformId || undefined,
    },
    { enabled: !!sectionDetails },
  );

  const isPositionDatesForbidden =
    positionDatesError?.response?.status === 403;

  // Apply available position dates whenever the fetch resolves or the active fund changes
  useEffect(() => {
    if (isPositionDatesForbidden) {
      setPositionDatesLoading(false);
      setPositionsDates([]);
      dispatch(updateAvailableDates([]));
      lastActiveFundIdRef.current = activeFundId;
      return;
    }

    // Only block the datepicker on initial load / no cached data — not background refetches
    if (isPositionDatesQueryLoading || !positionDatesMap) {
      setPositionDatesLoading(true);
      return;
    }

    const fundChanged = lastActiveFundIdRef.current !== activeFundId;
    const selectedDates: string[] = activeFundId
      ? (positionDatesMap[activeFundId] ?? [])
      : (positionDatesMap.overview ?? []);

    setPositionDatesLoading(false);
    setPositionsDates(selectedDates);
    dispatch(updateAvailableDates(selectedDates));
    lastActiveFundIdRef.current = activeFundId;

    if (fundChanged) {
      setDateRangeStart(getAvailableDateXMonthsAgo(3, selectedDates));
      setDateRangeEnd(getMostRecentAvailableDate(selectedDates));
    }
  }, [
    positionDatesMap,
    isPositionDatesQueryLoading,
    activeFundId,
    isPositionDatesForbidden,
    dispatch,
  ]);

  // every time the page changes, update the available dates.
  useEffect(() => {
    if (isPositionDatesForbidden) {
      setPositionsDates([]);
      dispatch(updateAvailableDates([]));
    } else if (positionDatesMap) {
      const selectedDates: string[] = activeFundId
        ? (positionDatesMap[activeFundId] ?? [])
        : (positionDatesMap.overview ?? []);
      setPositionsDates(selectedDates);
      dispatch(updateAvailableDates(selectedDates));
    }
    dispatch(updateActiveDateRangeStart(dateRangeStart));
    dispatch(updateActiveDateRangeEnd(dateRangeEnd));
  }, [pageId]);

  // =============================================================================
  // SIMPLIFIED DATE MANAGEMENT
  // =============================================================================
  // Rule 1: activeDate (Redux) is the single source of truth
  // Rule 2: URL positionDate is just a mirror of activeDate (one-way sync)
  // Rule 3: On initial hard load ONLY, URL can initialize activeDate
  // =============================================================================

  // EFFECT 1: Initialize activeDate when section changes or on initial load
  // This effect handles:
  // - Initial page load (honors URL positionDate if valid)
  // - Section changes (resets to section's most recent overview date)
  // - Same-section navigation (preserves existing date)
  useEffect(() => {
    // Wait for required data (cached data is enough — don't block on background refetches)
    if (!activeSection || !positionDatesMap) return;

    // Ensure sectionDetails matches activeSection to avoid stale dates
    if (sectionDetails?.sectionId !== activeSection) return;

    const sectionChanged = lastSectionRef.current !== activeSection;
    const isInitialLoad = isInitialLoadRef.current;
    const alreadyInitializedForSection =
      dateInitializedForSectionRef.current === activeSection;

    // Update tracking refs
    if (sectionChanged) {
      lastSectionRef.current = activeSection;
      dateInitializedForSectionRef.current = null;
    }
    if (isInitialLoad) {
      isInitialLoadRef.current = false;
    }

    // Skip if already initialized for this section
    if (alreadyInitializedForSection) return;

    // Skip if section unchanged and date already exists
    if (!sectionChanged && activeDate) {
      dateInitializedForSectionRef.current = activeSection;
      return;
    }

    // Determine what date to use
    let newDate: string | null = null;

    // Priority 1: URL positionDate on initial hard load only
    if (isInitialLoad && routeParamsRef.current.positionDate) {
      const urlDate = routeParamsRef.current.positionDate;
      const isValidFormat =
        dayjs(urlDate, DATE_FORMAT).format(DATE_FORMAT) === urlDate &&
        urlDate.length === 10;
      if (isValidFormat) {
        newDate = urlDate;
      }
    }

    // Priority 2: Section / fund dates
    // When section changes, always use overview dates (treat as fresh start)
    // Only use fund dates when navigating within the same section
    if (!newDate) {
      if (sectionChanged) {
        // Section changed - use overview dates (ignore persisted activeFund)
        newDate = positionDatesMap.overview?.[0] ?? null;
      } else if (activeFundId && positionDatesMap[activeFundId]?.[0]) {
        // Same section with fund - use fund's most recent date
        newDate = positionDatesMap[activeFundId]![0];
      } else {
        // Fallback to overview dates
        newDate = positionDatesMap.overview?.[0] ?? null;
      }
    }

    // Update state
    if (newDate) {
      dispatch(updateActiveDate(newDate));
      dateInitializedForSectionRef.current = activeSection;
    }

    // Update date range bounds
    dispatch(
      updateActiveDateRangeStart(getAvailableDateXMonthsAgo(3, positionDates)),
    );
    dispatch(
      updateActiveDateRangeEnd(getMostRecentAvailableDate(positionDates)),
    );
  }, [
    activeSection,
    sectionDetails,
    positionDatesMap,
    activeFundId,
    activeDate,
    positionDates,
    dispatch,
  ]);

  // EFFECT 2: Mirror activeDate to URL (one-way sync: Redux → URL)
  // This is the ONLY place that writes positionDate to URL
  useEffect(() => {
    // Only sync for pages that use position date picker
    if (!pageDetails?.usePositionDatePicker || !activeDate) return;

    const currentSearchParams = new URLSearchParams(location.search);
    const currentUrlDate = currentSearchParams.get('positionDate');

    // Skip if URL already matches
    if (currentUrlDate === activeDate) return;

    // Build target URL
    currentSearchParams.set('positionDate', activeDate);
    const targetUrl = `${location.pathname}?${currentSearchParams.toString()}`;

    // Skip if already synced (prevents feedback loop)
    if (lastSyncedUrlRef.current === targetUrl) return;

    // Sync to URL
    lastSyncedUrlRef.current = targetUrl;
    navigate(targetUrl, { replace: true });
  }, [
    pageDetails?.usePositionDatePicker,
    activeDate,
    location.pathname,
    location.search,
    navigate,
  ]);

  // NOTE: raptorPlatformId changes are now handled by the main initialization effect
  // via its dependency on dates (which changes when platform changes)

  // function to handle date choice made in datepicker
  const handlePositionDateChange = (date: Date | null) => {
    // first get relevant points in the current route
    const currentUrl = location.pathname + location.search;
    const indexOfPositionDate = currentUrl.indexOf('&positionDate');
    const newUrl =
      indexOfPositionDate !== -1
        ? currentUrl.slice(0, indexOfPositionDate)
        : currentUrl;
    const question = newUrl.indexOf('?') === -1 ? '?' : '';
    const and = newUrl.indexOf('fundId') === -1 ? '' : '';

    // then build a new route with date, and push new date to redux
    if (
      activeDate !== dayjs(date).format(DATE_FORMAT) &&
      positionDates.includes(dayjs(date).format(DATE_FORMAT))
    ) {
      navigate(
        newUrl +
        question +
        and +
        '&positionDate=' +
        dayjs(date).format(DATE_FORMAT),
      );
      dispatch(updateActiveDate(dayjs(date).format(DATE_FORMAT)));
    }
  };

  const titleBarSelectsAndInputs = (
    <>
      {selects &&
        selects.length > 0 &&
        selects.map((select: Select) => {
          const show = select.show ?? true;
          if (
            select.displayOnOverviewPage ||
            (!select.displayOnOverviewPage && activeFund)
          ) {
            if (show) {
              return (
                <TitleBarSelect
                  selectTitle={select.titleBarTitle}
                  key={select.titleBarKey}
                  labelsAndValues={select.values}
                  placeholderValue={select.placeholderValue}
                  selectedValue={select.currentValue}
                  selectKeyName={select.titleBarKey}
                />
              );
            } else {
              return null;
            }
          } else {
            return null;
          }
        })}
      {inputs &&
        inputs.length > 0 &&
        inputs.map((input: Input) => {
          if (
            input.displayOnOverviewPage ||
            (!input.displayOnOverviewPage && activeFund)
          ) {
            return (
              <GeneralInput
                setInput={(val) =>
                  dispatch(updateSpecificTitlebarInput(input.titleBarKey, val))
                }
                inputValue={input.currentValue}
                inputKey={input.titleBarKey}
                key={input.titleBarKey}
                inputTitle={input.titleBarTitle}
                width="1.25rem"
                defaultValue={input.defaultValue || ''}
                allowedValues={input.allowedValues}
              />
            );
          } else {
            return null;
          }
        })}
      {datePickers &&
        datePickers.length > 0 &&
        datePickers.map((picker: DatePicker, index: number) => {
          return (
            <RaptorDatePicker
              key={picker.titleBarKey}
              title={picker.titleBarTitle}
              handler={(val) =>
                dispatch(
                  updateSpecificTitleBarComponent(
                    picker.titleBarKey,
                    dayjs(val).format('YYYY-MM-DD'),
                    'datePickers',
                  ),
                )
              }
              datesToInclude={picker.availableDates || weekdays}
              selectedDate={dayjs(picker.currentValue)}
              tiedToActiveDate={false}
            />
          );
        })}
    </>
  );


  // Only Run this after toolbar contents change - avoids redux re-rendering etc
  useEffect(() => {
    checkForOverflow();
  }, [selects, inputs, datePickers]);

  return (
    <div
      role="toolbar"
      className="relative flex h-[3.75rem] w-full items-center justify-between overflow-hidden bg-white px-5 text-mainblue shadow-sm transition-transform duration-200"
      style={{ zIndex: zIndexes.titleBar }}
    >
      <h1 className="mr-4 text-nowrap text-2xl font-medium">{pageName}</h1>

      <div
        className="transition-opacity duration-200"
        style={{
          opacity: showLeftIndicator ? 1 : 0,
          pointerEvents: showLeftIndicator ? 'auto' : 'none',
        }}
      >
        <ScrollArrowButton
          direction="left"
          tooltip="More actions..."
          onClick={handleScrollLeft}
        />
      </div>
      <div className="relative w-full overflow-hidden">
        <div
          className="noscrollbar flex w-full items-center overflow-x-auto"
          ref={containerRef}
        >
          {showLeftIndicator && <FadeIndicator direction="left" />}

          {fundNames !== null ? (
            <>
              {activeSection &&
                pageDetails &&
                pageDetails.useFundPicker &&
                fundIds &&
                fundNames && (
                  <>
                    <FundPicker
                      currentPage={pageId || ''}
                      labels={fundNames}
                      values={fundIds}
                      currentlySelected={activeFund?.id || 'none_selected'}
                      section={activeSection}
                      pageId={pageId || ''}
                    />
                    {activeFund?.isDemoFund ? (
                      <Pill status="demo" className="mr-2 shrink-0">
                        Demo
                      </Pill>
                    ) : null}
                  </>
                )}
              {/* Standard date picker - hide when hideStandardDatePicker is true */}
              {pageDetails &&
                pageDetails.usePositionDatePicker &&
                !hideStandardDatePicker &&
                (isPositionDatesForbidden ? (
                  <span className="mr-2 text-sm text-red-600">
                    You are not authorised to access this resource.
                  </span>
                ) : !positionDatesLoading && positionDates.length > 0 ? (
                  <>
                    <RaptorDatePicker
                      key={`date-picker-${activeFundId || 'overview'}`}
                      title={'Position Date: '}
                      handler={handlePositionDateChange}
                      datesToInclude={positionDates}
                      selectedDate={
                        activeDate ? dayjs(activeDate) : dayjs(positionDates[0])
                      }
                      tiedToActiveDate={true}
                    />
                  </>
                ) : (
                  <RaptorLoading setWidth={'2.50rem'} />
                ))}

              {/* Standard date range picker */}
              {pageDetails &&
                pageDetails.usePositionDatePickerRange &&
                (isPositionDatesForbidden ? (
                  <span className="mr-2 text-sm text-red-600">
                    You are not authorised to access this resource.
                  </span>
                ) : !positionDatesLoading && positionDates.length > 0 ? (
                  <>
                    <RaptorDatePicker
                      key={`date-range-picker-start-${activeFundId || 'overview'}`}
                      title={'Start Date:'}
                      handler={(date: Date | null) => {
                        setDateRangeStart(dayjs(date).format(DATE_FORMAT));
                      }}
                      datesToInclude={positionDates}
                      selectedDate={dayjs(dateRangeStart)}
                      tiedToActiveDate={false}
                    />
                    <RaptorDatePicker
                      key={`date-range-picker-end-${activeFundId || 'overview'}`}
                      title={'End Date:'}
                      handler={(date: Date | null) => {
                        setDateRangeEnd(dayjs(date).format(DATE_FORMAT));
                      }}
                      datesToInclude={positionDates}
                      selectedDate={dayjs(dateRangeEnd)}
                      tiedToActiveDate={false}
                    />
                  </>
                ) : (
                  <RaptorLoading setWidth={'2.50rem'} />
                ))}
            </>
          ) : pageName && pageName !== 'Dashboard' ? (
            <RaptorLoading setWidth="2.50rem" />
          ) : null}
          {titleBarIsLoading ? (
            <RaptorLoading setWidth={'2.50rem'} />
          ) : (
            titleBarSelectsAndInputs
          )}

          {showRightIndicator && <FadeIndicator direction="right" />}
        </div>
      </div>

      <div
        className="transition-opacity duration-200"
        style={{
          opacity: showRightIndicator ? 1 : 0,
          pointerEvents: showRightIndicator ? 'auto' : 'none',
        }}
      >
        <ScrollArrowButton
          direction="right"
          tooltip="More actions..."
          onClick={handleScrollRight}
        />
      </div>
      {pageDetails?.exportable && <GeneralFileExportButton />}
    </div>
  );
};

export default TitleBar;
